import { Link, createFileRoute } from '@tanstack/react-router'
import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Upload,
  message,
} from 'antd'
import { SearchOutlined, UploadOutlined } from '@ant-design/icons'
import { Progress } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { useAllRegions } from '../hooks/useRegions'
import {
  useAddClassWithFile,
  useClasses,
  useDeleteClass,
  useUpdateClassWithFile,
} from '../hooks/useClasses'
import { useAllCourses } from '../hooks/useCourses'
import { getPagination } from '../components/TablePagination'
import type { ClassData, ClassQueryParams } from '../services/classService'
import type { ColumnType, FilterDropdownProps } from 'antd/es/table/interface'

export const Route = createFileRoute('/class')({
  component: ClassManagement,
})

function ClassManagement() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentClass, setCurrentClass] = useState<ClassData | null>(null)
  const [form] = Form.useForm()

  // 显示历史班级状态 - 默认不显示
  const [showHistoryClasses, setShowHistoryClasses] = useState<boolean>(() => {
    const search = new URLSearchParams(window.location.search)
    return search.get('showHistory') === 'true'
  })

  const [queryParams, setQueryParams] = useState<ClassQueryParams>(() => {
    const search = new URLSearchParams(window.location.search)
    return {
      pageNum: parseInt(search.get('pageNum') || '1', 10),
      pageSize: parseInt(search.get('pageSize') || '10', 10),
      name: search.get('name') || undefined,
      courseId: search.get('courseId')
        ? parseInt(search.get('courseId')!, 10)
        : undefined,
      regionId: search.get('regionId')
        ? parseInt(search.get('regionId')!, 10)
        : undefined,
    }
  })

  useEffect(() => {
    const params = new URLSearchParams()
    if (queryParams.name) params.set('name', queryParams.name)
    if (queryParams.courseId)
      params.set('courseId', queryParams.courseId.toString())
    if (queryParams.regionId)
      params.set('regionId', queryParams.regionId.toString())
    if (queryParams.pageNum)
      params.set('pageNum', queryParams.pageNum.toString())
    if (queryParams.pageSize)
      params.set('pageSize', queryParams.pageSize.toString())
    if (showHistoryClasses) params.set('showHistory', 'true')
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState(null, '', newUrl)
  }, [queryParams, showHistoryClasses])

  // 动态查询参数 - 根据 showHistoryClasses 决定是否添加 afterEndTime
  const apiQueryParams = useMemo(() => {
    const params = { ...queryParams }
    if (!showHistoryClasses) {
      // 默认只显示未截止的班级（基于课程的结束时间）
      params.afterEndTime = dayjs().format('YYYY-MM-DDTHH:mm:ss')
    }
    return params
  }, [queryParams, showHistoryClasses])

  const [qrcodeFile, setQrcodeFile] = useState<File | null>(null)
  const [qrcodePreview, setQrcodePreview] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // API hooks
  const {
    data: classesResponse,
    isLoading: classesLoading,
    refetch,
  } = useClasses(apiQueryParams)
  const { data: regionsData } = useAllRegions()
  const { data: coursesData } = useAllCourses()
  const deleteClassMutation = useDeleteClass()
  const addClassMutation = useAddClassWithFile()
  const updateClassMutation = useUpdateClassWithFile()

  const regions = regionsData || []
  const courses = coursesData || []
  const classes = classesResponse?.records || []
  const total = classesResponse?.total || 0

  const columns: Array<ColumnType<ClassData>> = [
    {
      title: '班级 ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '班级名称',
      dataIndex: 'name',
      key: 'name',
      filteredValue: queryParams.name ? [queryParams.name] : undefined,
      filterDropdown: (props: FilterDropdownProps) => {
        const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="搜索班级名称"
              value={selectedKeys[0] as string}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => {
                confirm({ closeDropdown: true })
              }}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm({ closeDropdown: true })}
                size="small"
                style={{ width: 90 }}
              >
                搜索
              </Button>
              <Button
                onClick={() => {
                  clearFilters && clearFilters()
                  confirm({ closeDropdown: true })
                }}
                size="small"
                style={{ width: 90 }}
              >
                重置
              </Button>
            </Space>
          </div>
        )
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: '课程名称',
      dataIndex: 'courseName',
      key: 'courseName',
      filters: Array.isArray(courses)
        ? courses.map((course: any) => ({
            text: course.name,
            value: course.id,
          }))
        : [],
      onFilter: (value: any, record: ClassData) => record.courseId === value,
      filteredValue: queryParams.courseId ? [queryParams.courseId] : undefined,
      render: (text: string) => (
        <Link to="/course" search={{ name: text }}>
          {text}
        </Link>
      ),
    },
    {
      title: '剩余名额',
      key: 'remainingCapacity',
      render: (_: any, record: ClassData) => {
        const remaining = record.totalInventory - record.signUpNum
        if (remaining <= 0) {
          return <span style={{ color: '#ff4d4f', fontWeight: 600 }}>已售罄</span>
        }
        const color =
          remaining <= 5 ? '#ff4d4f' : remaining <= 10 ? '#faad14' : undefined
        return (
          <span style={{ color, fontWeight: 600 }}>{remaining}</span>
        )
      },
    },
    {
      title: '容量进度',
      key: 'capacityProgress',
      render: (_: any, record: ClassData) => {
        if (record.totalInventory === 0) return '-'
        const remaining = record.totalInventory - record.signUpNum
        const percent = Math.round((record.signUpNum / record.totalInventory) * 100)
        let strokeColor = '#52c41a'
        if (remaining <= 0) strokeColor = '#ff4d4f'
        else if (remaining <= 5) strokeColor = '#ff4d4f'
        else if (remaining <= 10) strokeColor = '#faad14'
        return <Progress percent={percent} strokeColor={strokeColor} size="small" />
      },
    },
    {
      title: '状元数量',
      dataIndex: 'championInventory',
      key: 'championInventory',
      sorter: (a: ClassData, b: ClassData) =>
        (a.championInventory || 0) - (b.championInventory || 0),
    },
    {
      title: '地区',
      dataIndex: 'regionName',
      key: 'regionName',
      render: (regionName: string) => <Link to="/region">{regionName}</Link>,
    },
    {
      title: '总名额',
      dataIndex: 'totalInventory',
      key: 'totalInventory',
      sorter: (a: ClassData, b: ClassData) =>
        a.totalInventory - b.totalInventory,
    },
    {
      title: '状元名额',
      dataIndex: 'championInventory',
      key: 'championInventory',
      sorter: (a: ClassData, b: ClassData) =>
        a.championInventory - b.championInventory,
    },
    {
      title: '已报名人数',
      dataIndex: 'signUpNum',
      key: 'signUpNum',
      sorter: (a: ClassData, b: ClassData) => a.signUpNum - b.signUpNum,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record: ClassData) => (
        <Space size="middle">
          <Button size="small" onClick={() => showEditModal(record)}>
            编辑
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  const showAddModal = () => {
    setCurrentClass(null)
    setQrcodeFile(null)
    setQrcodePreview(null)
    form.resetFields()
    setIsModalOpen(true)
  }

  const showEditModal = async (classItem: ClassData) => {
    setCurrentClass(classItem)
    setQrcodeFile(null)

    // 如果有二维码URL，预览显示，但编辑时需要重新上传
    if (classItem.qrcode) {
      setQrcodePreview(classItem.qrcode)
    } else {
      setQrcodePreview(null)
    }

    form.setFieldsValue({
      name: classItem.name,
      courseId: classItem.courseId,
      regionId: Array.isArray(regions)
        ? regions.find((r) => r.name === classItem.regionName)?.id
        : undefined,
      totalInventory: classItem.totalInventory,
      championInventory: classItem.championInventory,
    })
    setIsModalOpen(true)
  }

  const handleOk = () => {
    form.validateFields().then((values) => {
      setIsSubmitting(true)
      const formData = new FormData()

      if (currentClass) {
        // 更新 - 只传递有变化的字段
        formData.append('id', currentClass.id.toString())

        let hasChanges = false

        // 检查每个字段是否有变化，只传递变化的字段
        if (values.name !== currentClass.name) {
          formData.append('name', values.name)
          hasChanges = true
        }
        if (values.courseId !== currentClass.courseId) {
          formData.append('courseId', values.courseId.toString())
          hasChanges = true
        }
        const currentRegionId = Array.isArray(regions)
          ? regions.find((r) => r.name === currentClass.regionName)?.id
          : undefined
        if (values.regionId !== currentRegionId) {
          formData.append('regionId', values.regionId.toString())
          hasChanges = true
        }
        if (values.totalInventory !== currentClass.totalInventory) {
          formData.append('totalInventory', values.totalInventory.toString())
          hasChanges = true
        }
        if (values.championInventory !== currentClass.championInventory) {
          formData.append(
            'championInventory',
            values.championInventory.toString(),
          )
          hasChanges = true
        }

        // 只有在用户重新选择了二维码文件时才传递图片
        if (qrcodeFile) {
          formData.append('qrcode', qrcodeFile)
          hasChanges = true
        }

        // 如果没有任何变化，直接关闭模态框
        if (!hasChanges) {
          setIsSubmitting(false)
          setIsModalOpen(false)
          message.info('没有任何修改')
          return
        }

        // 调用更新接口
        updateClassMutation.mutate(formData, {
          onSuccess: () => {
            setIsModalOpen(false)
            refetch()
          },
          onError: (error: Error) => {
            console.error('更新班级失败:', error)
          },
          onSettled: () => {
            setIsSubmitting(false)
          },
        })
      } else {
        // 新增 - 传递所有字段
        formData.append('name', values.name)
        formData.append('courseId', values.courseId.toString())
        formData.append('regionId', values.regionId.toString())
        formData.append('totalInventory', values.totalInventory.toString())
        formData.append(
          'championInventory',
          values.championInventory.toString(),
        )

        // 新增时如果有二维码文件则传递
        if (qrcodeFile) {
          formData.append('qrcode', qrcodeFile)
        }

        addClassMutation.mutate(formData, {
          onSuccess: () => {
            setIsModalOpen(false)
            refetch()
          },
          onError: (error: Error) => {
            console.error('添加班级失败:', error)
          },
          onSettled: () => {
            setIsSubmitting(false)
          },
        })
      }
    })
  }

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除该班级吗？',
      onOk: () => {
        deleteClassMutation.mutate(id, {
          onSuccess: () => {
            refetch()
          },
        })
      },
    })
  }

  const handleTableChange = (pagination: any, filters: any) => {
    const nameFilter = (filters.name as Array<string>)?.[0]
    const courseFilter = filters.courseName?.[0]
    setQueryParams({
      pageNum: pagination.current,
      pageSize: pagination.pageSize,
      name: nameFilter,
      courseId: courseFilter,
      regionId: queryParams.regionId, // preserve other params
    })
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">班级管理</h2>
        <Space>
          <Checkbox
            checked={showHistoryClasses}
            onChange={(e) => setShowHistoryClasses(e.target.checked)}
          >
            显示历史班级
          </Checkbox>
          <Button type="primary" onClick={showAddModal}>
            新增班级
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={classes}
        rowKey="id"
        bordered
        loading={classesLoading}
        pagination={getPagination(
          queryParams.pageNum ?? 1,
          queryParams.pageSize ?? 10,
          total,
        )}
        onChange={handleTableChange}
      />

      <Modal
        title={currentClass ? '编辑班级' : '新增班级'}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        width={800}
        confirmLoading={isSubmitting}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="班级名称"
            rules={[{ required: true, message: '请输入班级名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="courseId"
            label="关联课程"
            rules={[{ required: true, message: '请选择关联课程' }]}
          >
            <Select placeholder="请选择关联课程">
              {Array.isArray(courses)
                ? courses.map((course: any) => (
                    <Select.Option key={course.id} value={course.id}>
                      {course.name}
                    </Select.Option>
                  ))
                : null}
            </Select>
          </Form.Item>
          <Form.Item
            name="regionId"
            label="地区"
            rules={[{ required: true, message: '请选择地区' }]}
          >
            <Select placeholder="请选择地区">
              {Array.isArray(regions)
                ? regions.map((region) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.name}
                    </Select.Option>
                  ))
                : null}
            </Select>
          </Form.Item>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="totalInventory"
              label="总名额"
              rules={[{ required: true, message: '请输入总名额' }]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                onChange={() => {
                  // Re-validate championInventory when totalInventory changes
                  form.validateFields(['championInventory'])
                }}
              />
            </Form.Item>
            <Form.Item
              name="championInventory"
              label="状元名额"
              rules={[
                { required: true, message: '请输入状元名额' },
                { type: 'number', min: 1, message: '状元名额必须大于0' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const totalInventory = getFieldValue('totalInventory')
                    if (value && totalInventory && value > totalInventory) {
                      return Promise.reject(new Error('状元名额不能大于总名额'))
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <Form.Item name="qrcode" label="班级群二维码">
            <Upload
              name="file"
              listType="picture"
              showUploadList={false}
              beforeUpload={(file) => {
                const isJpgOrPng =
                  file.type === 'image/jpeg' || file.type === 'image/png'
                if (!isJpgOrPng) {
                  message.error('只能上传 JPG/PNG 格式的图片!')
                  return false
                }
                const isLt2M = file.size / 1024 / 1024 < 2
                if (!isLt2M) {
                  message.error('图片大小不能超过 2MB!')
                  return false
                }

                // 保存文件用于表单提交
                setQrcodeFile(file)

                // 创建预览URL
                const reader = new FileReader()
                reader.onload = (e) => {
                  setQrcodePreview(e.target?.result as string)
                }
                reader.readAsDataURL(file)

                return false // 阻止自动上传
              }}
            >
              <Button icon={<UploadOutlined />}>选择二维码</Button>
            </Upload>
            {qrcodePreview && (
              <div className="mt-2">
                <img
                  src={qrcodePreview}
                  alt="二维码预览"
                  style={{ width: 100, height: 100, objectFit: 'cover' }}
                />
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default ClassManagement
